import React, { useEffect, useMemo, useState } from 'react';
import { Box, TextField, Autocomplete } from '@mui/material';
import { useTranslation } from 'react-i18next';
import bundledPresets from '../../data/aiProviderPresets.json';

// #95: `bundledPresets` ist der Stand zum Build-Zeitpunkt (fuer Tests und den
// ersten Render). Die massgebliche Liste liegt als `aiProviderPresets.json`
// im `public`-Ordner und wird hier zur Laufzeit nachgeladen -- eine neue
// Modell-ID braucht so kein Deployment der Anwendung, nur eine ausgetauschte
// Datei auf dem Server.
const PRESETS_URL = `${process.env.PUBLIC_URL}/aiProviderPresets.json`;

const isValidPresetList = (data) =>
  Array.isArray(data)
  && data.every(
    (preset) =>
      preset
      && typeof preset.id === 'string'
      && typeof preset.label === 'string'
      && Array.isArray(preset.models),
  );

const findPresetByProvider = (presets, provider) => {
  const normalized = (provider || '').trim().toLowerCase();
  if (!normalized) return null;
  return (
    presets.find(
      (preset) => preset.label.toLowerCase() === normalized || preset.id === normalized,
    ) || null
  );
};

const AiProviderSettings = ({
  value = { provider: '', model: '', endpoint: '' },
  onChange = () => {},
}) => {
  const { t } = useTranslation();
  const [presets, setPresets] = useState(bundledPresets);

  useEffect(() => {
    if (typeof fetch !== 'function') return undefined;
    let cancelled = false;
    fetch(PRESETS_URL)
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((data) => {
        if (!cancelled && isValidPresetList(data)) setPresets(data);
      })
      .catch(() => {
        // Laeuft die Laufzeit-Datei nicht (kein Server, Testumgebung, 404),
        // bleibt die eingebaute Liste als Vorschlag -- AK2 gilt so oder so.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const matchedPreset = useMemo(() => findPresetByProvider(presets, value.provider), [presets, value.provider]);
  const modelOptions = matchedPreset ? matchedPreset.models : [];
  const showEndpointField = Boolean(matchedPreset?.requiresEndpoint);

  const handleProviderChange = (_event, nextProvider) => {
    const provider = nextProvider || '';
    const nextPreset = findPresetByProvider(presets, provider);
    onChange({
      ...value,
      provider,
      endpoint:
        nextPreset?.requiresEndpoint && !value.endpoint
          ? nextPreset.defaultEndpoint
          : value.endpoint,
    });
  };

  const handleModelChange = (_event, nextModel) => {
    onChange({ ...value, model: nextModel || '' });
  };

  const handleEndpointChange = (event) => {
    onChange({ ...value, endpoint: event.target.value });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
      <Autocomplete
        freeSolo
        options={presets.map((preset) => preset.label)}
        inputValue={value.provider}
        onInputChange={handleProviderChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('settings.sections.api.aiProvider.fields.provider')}
            placeholder={t('settings.sections.api.aiProvider.fields.providerPlaceholder')}
            size="small"
          />
        )}
      />
      <Autocomplete
        freeSolo
        options={modelOptions}
        inputValue={value.model}
        onInputChange={handleModelChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('settings.sections.api.aiProvider.fields.model')}
            placeholder={t('settings.sections.api.aiProvider.fields.modelPlaceholder')}
            size="small"
          />
        )}
      />
      {showEndpointField && (
        <TextField
          label={t('settings.sections.api.aiProvider.fields.endpoint')}
          placeholder={matchedPreset?.defaultEndpoint}
          value={value.endpoint || ''}
          onChange={handleEndpointChange}
          size="small"
        />
      )}
    </Box>
  );
};

export default AiProviderSettings;
